import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { LogoMark } from "@/components/AuthShell";
import { Apple, Monitor, Download, Loader2, LogIn, ArrowRight, KeyRound, Headphones, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API, errMsg } from "@/lib/api";
import { UnsignedHelp } from "@/components/UnsignedHelp";

export const BrandMark = ({ brand, onClick }) => (
  <Link to={brand?.slug ? `/join/${brand.slug}` : "/join"} onClick={onClick} className="flex items-center gap-3 min-w-0" data-testid="join-logo">
    {brand?.logo_version ? (
      <img src={`${API}/public/brand/${brand.slug}/logo?v=${brand.logo_version}`} alt={brand.name} className="h-10 max-w-[180px] object-contain" data-testid="join-brand-logo" />
    ) : (
      <LogoMark className="h-9 w-9 shrink-0" variant="color" />
    )}
    <span className="font-semibold text-lg text-slate-900 truncate" data-testid="join-brand-name">{brand?.name || "Assistance à distance"}</span>
  </Link>
);

export default function Join() {
  const params = useParams();
  const isSlug = params.code && !/^\d[\d\s]*$/.test(params.code);
  const [code, setCode] = useState(isSlug ? "" : params.code || "");
  const [brand, setBrand] = useState(null);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.body.classList.add("theme-light");
    return () => document.body.classList.remove("theme-light");
  }, []);

  const check = async (c) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/support/join/${c}`);
      setInfo(data);
      setBrand(data.brand);
    } catch (e) {
      setInfo(null);
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!params.code) return;
    if (isSlug) api.get(`/public/brand/${params.code}`).then(({ data }) => { setBrand(data); if (data.slug && data.slug !== params.code.toLowerCase()) window.history.replaceState(null, "", `/join/${data.slug}`); }).catch(() => setBrand(null));
    else check(params.code);
  }, [params.code, isSlug]);
  useEffect(() => { document.title = brand?.name ? `${brand.name} · Assistance` : "Assistance à distance"; }, [brand]);

  const clean = code.replace(/\D/g, "");
  const launcher = (kind) => `${API}/support/launcher/${clean}/${kind}`;

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex flex-col" data-testid="join-page">
      <header className="h-16 bg-white border-b border-slate-200 px-6 sm:px-10 flex items-center justify-between gap-4">
        <BrandMark brand={brand} onClick={() => { setInfo(null); setCode(""); setError(""); }} />
        <Link to="/login" data-testid="join-login-link" className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#2563EB] transition-colors">
          <LogIn className="h-4 w-4" /> Se connecter
        </Link>
      </header>
      <main className="flex-1 flex flex-col items-center px-6 pt-16 sm:pt-24 pb-12">
        {!info ? (
          <form onSubmit={(e) => { e.preventDefault(); check(clean); }} className="w-full max-w-md text-center animate-in fade-in slide-in-from-bottom-2 duration-500">
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 mb-3">Rejoindre une session</h1>
            <p className="text-slate-600 mb-10">Saisissez le code à 6 chiffres communiqué par votre technicien.</p>
            <Input data-testid="join-code-input" autoFocus inputMode="numeric" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} placeholder="000 000" className="h-20 bg-white text-4xl md:text-5xl text-center font-mono tracking-[0.3em] border-2 border-slate-300 focus-visible:border-[#2563EB] focus-visible:ring-0 shadow-sm" />
            {error && <p className="text-sm text-red-600 mt-3" data-testid="join-error">{error}</p>}
            <Button data-testid="join-submit-btn" type="submit" disabled={clean.length !== 6 || busy} className="w-full mt-6 h-12 text-base bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />} Rejoindre
            </Button>
          </form>
        ) : (
          <div className="w-full max-w-lg bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-5" data-testid="join-ready">
            <p className="text-slate-700"><b>{info.org_name}</b> vous propose une session d'assistance : <b>{info.name}</b>.</p>
            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <p className="font-semibold flex items-center gap-2"><Monitor className="h-5 w-5 text-blue-600" /> Windows</p>
              <Button asChild className="w-full"><a data-testid="join-download-windows" href={launcher(info.binaries ? "windows-exe" : "windows-bat")}><Download className="h-4 w-4 mr-2" /> Télécharger l'outil d'assistance</a></Button>
              <p className="text-sm text-slate-600">Ouvrez le fichier téléchargé, puis acceptez si Windows demande une confirmation.</p>
              <UnsignedHelp os="windows" />
            </div>
            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <p className="font-semibold flex items-center gap-2"><Apple className="h-5 w-5 text-blue-600" /> Mac</p>
              <Button asChild className="w-full"><a data-testid="join-download-mac" href={launcher("mac-sh")}><Download className="h-4 w-4 mr-2" /> Télécharger l'outil d'assistance</a></Button>
              <p className="text-sm text-slate-600">Double-cliquez sur le fichier téléchargé.</p>
              <UnsignedHelp os="mac" />
            </div>
            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <p className="font-semibold flex items-center gap-2"><Terminal className="h-5 w-5 text-blue-600" /> Linux</p>
              <Button asChild className="w-full"><a data-testid="join-download-linux" href={launcher("linux-sh")}><Download className="h-4 w-4 mr-2" /> Télécharger l'outil d'assistance</a></Button>
              <p className="text-sm text-slate-600">Ouvrez un terminal dans le dossier du fichier puis lancez : <code className="bg-slate-100 px-1 rounded">bash Assistance-*.sh</code>. Session graphique X11 requise.</p>
            </div>
            <p className="text-xs text-slate-500">Une petite fenêtre s'ouvrira pendant la session. Fermez-la pour arrêter : rien ne reste installé.</p>
          </div>
        )}
        <ol className="mt-16 w-full max-w-3xl grid gap-4 sm:grid-cols-3" data-testid="join-steps">
          {STEPS.map(([Icon, title, text], i) => (
            <li key={title} className="bg-white rounded-lg border border-slate-200 p-5 text-left">
              <div className="flex items-center gap-2 mb-2">
                <span className="h-6 w-6 rounded-full bg-[#2563EB]/10 text-[#2563EB] text-xs font-semibold grid place-items-center">{i + 1}</span>
                <Icon className="h-4 w-4 text-slate-500" />
                <span className="font-medium text-slate-900 text-sm">{title}</span>
              </div>
              <p className="text-sm text-slate-600">{text}</p>
            </li>
          ))}
        </ol>
      </main>
    </div>
  );
}

const STEPS = [
  [KeyRound, "Entrez le code", "Votre technicien vous communique un code à 6 chiffres."],
  [Download, "Lancez l'outil", "Téléchargez et ouvrez le petit outil d'assistance."],
  [Headphones, "On s'occupe de tout", "Le technicien prend la main avec votre accord."],
];
